import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  BookOpenCheck,
  CheckCircle2,
  Clock,
  Flame,
  ShieldCheck,
  Users,
  Video,
} from "lucide-react";
import {
  MASTERCLASS_BOOK_URL,
  MASTERCLASS_PRICE,
  PRIMARY_CTAS,
} from "@/content/primary-cta";
import { trackPlacementCTA } from "@/lib/cta-placement";
import { SiteNav } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PageMeta } from "@/components/site/PageMeta";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { buildFAQPageJsonLd } from "@/content/faq";
import { organizationNode, publisherRef } from "@/lib/entity-jsonld";
import {
  buildFreeMasterclassCourseInstanceJsonLd,
  buildFreeMasterclassEventJsonLd,
} from "@/content/masterclass-event";

import { ConversionBand } from "@/components/site/ConversionBand";
import { HeroPortrait } from "@/components/masterclass/HeroPortrait";
import { HeroPreload } from "@/components/site/HeroPreload";
import { MakeChangeMotif } from "@/components/masterclass/MakeChangeMotif";
import { MasterclassRegistrationForm } from "@/components/masterclass/RegistrationForm";

import { Reveal } from "@/hooks/useReveal";
import { captureAttribution } from "@/lib/utm";
import { trackMasterclassCTA, trackMasterclassView } from "@/lib/masterclass-analytics";
import { trackHeroCTAClick } from "@/lib/hero-analytics";

// Same responsive pipeline as the homepage hero: bounded 1024w baseline plus
// AVIF → WebP → JPEG srcsets and a tiny LQIP for the blur-up.
import heroImg from "../assets/dr-sully-hero-blend.jpg?format=jpg&quality=76&w=1024";
import heroAvifSrcSet from "../assets/dr-sully-hero-blend.jpg?format=avif&quality=60&w=320;360;480;640;768;1024;1360;1600&as=srcset";
import heroWebpSrcSet from "../assets/dr-sully-hero-blend.jpg?format=webp&quality=70&w=320;360;480;640;768;1024;1360;1600&as=srcset";
import heroJpgSrcSet from "../assets/dr-sully-hero-blend.jpg?format=jpg&quality=76&w=320;360;480;640;768;1024;1360;1600&as=srcset";
import heroPlaceholder from "../assets/dr-sully-hero-blend.jpg?format=webp&quality=40&w=24";
import { OFFERS } from "@/content/offers";
import { IMAGE_ALT } from "@/content/image-alt";

// Must mirror the rendered slot width at every breakpoint, otherwise the
// browser fetches a candidate that doesn't match the layout.
const HERO_SIZES =
  "(min-width: 1280px) 420px, (min-width: 1024px) 38vw, (min-width: 640px) 60vw, 100vw";

const SITE_URL = "https://drsullyspeaks.com";
const MASTERCLASS_URL = `${SITE_URL}/masterclass`;
// Primary CTA copy/destination is shared with the sticky bar — see
// src/content/primary-cta.ts. Never re-word it locally.
const BOOK_URL = MASTERCLASS_BOOK_URL;
import { NEXT_MASTERCLASS, formatEventDate } from "@/content/events";
const PRICE = MASTERCLASS_PRICE;
const PRIMARY = PRIMARY_CTAS.masterclass;
const CURRENCY = "USD";


const INCLUDES = [
  { icon: Video, label: "90-minute live masterclass with Dr. Sully" },
  { icon: BookOpenCheck, label: "Lifetime access to the full replay + transcript" },
  { icon: BookOpenCheck, label: "The Built On Purpose workbook (PDF, print-ready)" },
  { icon: Users, label: "30 days inside the MAKECHANGE Circle community" },
  { icon: Flame, label: "The 10-pillar MAKECHANGE implementation map" },
  { icon: ShieldCheck, label: "No upsells, no pressure, no fluff" },
];

const CURRICULUM = [
  { pillar: "Momentum", promise: "Break the freeze cycle and ship the first real move this week." },
  { pillar: "Authority", promise: "Name your seat of expertise so buyers stop confusing you with generalists." },
  { pillar: "Kingdom-Focus", promise: "Anchor your work to a mission bigger than the transaction." },
  { pillar: "Equity", promise: "Price and package so the exchange is fair for you and the client." },
  { pillar: "Clarity", promise: "Turn a 30-minute pitch into a single-sentence offer." },
  { pillar: "Healing", promise: "Address the trauma pattern that keeps sabotaging the next tier." },
  { pillar: "Action", promise: "Convert insight into calendared, measurable execution." },
  { pillar: "Navigation", promise: "Route around gatekeepers instead of collecting bruises from them." },
  { pillar: "Growth", promise: "Scale without turning yourself into the bottleneck." },
  { pillar: "Empowerment", promise: "Build a legacy asset — not a job you can never leave." },
];

const FAQS = [
  {
    q: "Who is the Built On Purpose Masterclass for?",
    a: "Founders, executives, pastors, and mission-driven operators who already have expertise but keep hitting a ceiling on clarity, monetization, or momentum. If you are pre-revenue with no offer at all, start with the Free Scan first.",
  },
  {
    q: "How is this different from a webinar or a course?",
    a: "It is a 90-minute working session built on the 10-pillar MAKECHANGE framework Dr. Sully uses with 1:1 executive coaching clients. You leave with a written implementation map, not a folder of PDFs you will never open.",
  },
  {
    q: `What do I actually get for ${OFFERS.masterclass.priceLabel}?`,
    a: "Live 90-minute masterclass with Dr. Sully, lifetime replay and transcript, the Built On Purpose workbook, the 10-pillar implementation map, and 30 days inside the MAKECHANGE Circle community.",
  },
  {
    q: "Do I need to attend live?",
    a: "No. Every registrant receives lifetime access to the full replay and transcript. Attend live for the interactive workshop; watch on replay if the time zone does not work.",
  },
  {
    q: "Is there a refund policy?",
    a: "Digital products are non-refundable once delivered. If you attend live, complete the workbook, and can show your work in the Circle, and it did not deliver, message the team within 14 days and we will make it right.",
  },
  {
    q: "Is the masterclass faith-informed?",
    a: "Yes. Dr. Sully is openly Christ-centered, and the Kingdom-Focus pillar reflects that. The frameworks work for clients of all faiths and none — no religious affiliation is required to attend or benefit.",
  },
  {
    q: "Can I expense this or pay with a company card?",
    a: "Yes. Checkout accepts any major card. A receipt is emailed instantly and includes a line item suitable for professional development or continuing education budgets.",
  },
];

const JSON_LD = {
  "@context": "https://schema.org",
  "@graph": [
    // Canonical brand entity — every provider/publisher reference on this page
    // resolves here, so Google attributes the course to the same Organization
    // as the rest of the site.
    organizationNode(),
    {
      "@type": "Course",
      "@id": `${MASTERCLASS_URL}#course`,
      name: "Built On Purpose Masterclass",
      description:
        "A 90-minute live masterclass with Dr. Sully (PsyD · ThD · DMin · CERC) covering the 10-pillar MAKECHANGE framework, plus lifetime replay, workbook, and 30 days in the MAKECHANGE Circle.",
      url: MASTERCLASS_URL,
      image: `${SITE_URL}/og/masterclass.jpg`,
      inLanguage: "en",
      educationalLevel: "Professional",
      teaches: CURRICULUM.map((c) => `${c.pillar}: ${c.promise}`),
      // Points at the ONE canonical Organization declared in this graph (and
      // on index.html) instead of a duplicate anonymous publisher.
      provider: publisherRef,
      publisher: publisherRef,
      hasCourseInstance: [
        {
          "@type": "CourseInstance",
          courseMode: "Online",
          courseWorkload: "PT2H",
          inLanguage: "en",
          location: {
            "@type": "VirtualLocation",
            url: MASTERCLASS_URL,
          },
        },
        // Free live session — dates are placeholders until each cohort is set
        // (override via VITE_FREE_MASTERCLASS_START/END).
        buildFreeMasterclassCourseInstanceJsonLd(),
      ],
      offers: {
        "@type": "Offer",
        price: String(PRICE),
        priceCurrency: CURRENCY,
        availability: "https://schema.org/InStock",
        category: "Paid",
        url: /^https?:\/\//.test(BOOK_URL) ? BOOK_URL : `${SITE_URL}${BOOK_URL}`,
      },
    },
    // Free live masterclass as a standalone Event with registration URL.
    buildFreeMasterclassEventJsonLd(`${MASTERCLASS_URL}#free-event`),
    // Rendered by the FAQ section below and marked up from the SAME array —
    // Google requires every Q/A in structured data to be visible on the page.
    buildFAQPageJsonLd(FAQS, `${MASTERCLASS_URL}#faq`),


    {
      "@type": "BreadcrumbList",
      "@id": `${MASTERCLASS_URL}#breadcrumbs`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Invest", item: `${SITE_URL}/invest` },
        { "@type": "ListItem", position: 3, name: "Built On Purpose Masterclass", item: MASTERCLASS_URL },
      ],
    },
  ],
};

const Masterclass = () => {
  // Capture campaign params on entry so every downstream event (CTA click,
  // checkout submit, paid confirmation) carries the same attribution.
  const viewedRef = useRef(false);
  useEffect(() => {
    if (viewedRef.current) return;
    viewedRef.current = true;
    captureAttribution();
    trackMasterclassView({ page: "/masterclass" });
  }, []);

  return (
  <div className="min-h-screen bg-background text-foreground">
    <PageMeta
      title="Built On Purpose Masterclass — 90 Minutes with Dr. Sully"
      description={`A live 90-minute masterclass on the 10-pillar MAKECHANGE framework with Dr. Sully (PsyD · ThD · DMin · CERC). Lifetime replay, workbook, and 30 days in the MAKECHANGE Circle. ${OFFERS.masterclass.priceLabel}.`}
      path="/masterclass"
      ogType="product"
      image="/og/masterclass.jpg"
      imageAlt="Dr. Sully (PsyD · ThD · DMin · CERC) — Built On Purpose Masterclass, 90-minute live session on the 10-pillar MAKECHANGE framework"
      jsonLd={JSON_LD}
    />
    {/* Starts the LCP portrait download during HTML parse rather than after
        hydration. Candidate set + sizes mirror <HeroPortrait /> exactly so the
        preload resolves to the same file the <picture> element picks. */}
    <HeroPreload src={heroImg} imageSrcSet={heroAvifSrcSet} sizes={HERO_SIZES} />
    <SiteNav />

    <section data-hero-section className="relative pt-32 pb-16 md:pt-40 md:pb-20 bg-hero overflow-hidden">
      {/* Secondary visual motif. Purely decorative here — the hero already
          names MAKECHANGE in copy, so the glyphs stay behind the content at a
          low intensity and never compete with the LCP portrait. */}
      <MakeChangeMotif variant="scatter" intensity={0.55} scale={0.75} className="md:[--mc-motif-scale:1]" />
      <div className="container max-w-6xl relative z-10">
        {/* Mirrors the BreadcrumbList in JSON_LD — Google only shows breadcrumb
            rich results when the trail is also visible on the page. */}
        <Breadcrumbs
          className="mb-8"
          items={[
            { name: "Home", href: "/" },
            { name: "Invest", href: "/invest" },
            { name: "Built On Purpose Masterclass" },
          ]}
        />
      </div>
      <div className="container max-w-6xl relative z-10 grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
        <div className="lg:col-span-7">
        <Reveal variant="up" className="text-center lg:text-left">
          <span className="eyebrow !mx-auto lg:!mx-0">Live Masterclass · ${PRICE}</span>
          <h1 className="font-display font-bold uppercase text-white text-4xl md:text-6xl mt-4 leading-[1.05]">
            Built On <span className="text-gradient-gold">Purpose.</span>
          </h1>
          <p className="text-silver text-base md:text-lg mt-5 max-w-xl mx-auto lg:mx-0 leading-relaxed">
            90 minutes with Dr. Sully to turn scattered expertise into a
            fundable, repeatable offer — using the same 10-pillar
            <strong className="text-white"> MAKECHANGE</strong> framework
            that runs his 1:1 executive coaching.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
            <Link
              to={BOOK_URL}
              onClick={() =>
                {
                  trackMasterclassCTA("masterclass_hero_reserve", { position: "hero", intent: true });
                  trackHeroCTAClick("masterclass_hero", "masterclass_hero_reserve", { destination: BOOK_URL });
                  trackPlacementCTA("hero", PRIMARY, { surface: "masterclass_hero" });
                }
              }
              className="inline-flex items-center justify-center gap-2 bg-cta text-white font-display uppercase tracking-[0.2em] text-xs px-6 py-4 shadow-cta hover:brightness-110 transition-all"
            >
              {PRIMARY.label}
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
            <Link
              to="/scan"
              onClick={() => {
                trackMasterclassCTA("masterclass_hero_scan", { position: "hero" });
                trackHeroCTAClick("masterclass_hero", "masterclass_hero_scan", { destination: "/scan" });
              }}
              className="inline-flex items-center justify-center gap-2 border border-gold/40 text-white font-display uppercase tracking-[0.2em] text-xs px-6 py-4 hover:bg-gold/10 transition-all"
            >
              Take the Free Scan First
            </Link>
          </div>

          <p className="mt-6 text-center lg:text-left text-sm text-white">
            <span className="text-gold font-display uppercase tracking-[0.2em] text-xs">Next live class · </span>
            {formatEventDate(NEXT_MASTERCLASS.startsAt).date} · {formatEventDate(NEXT_MASTERCLASS.startsAt).time} · {NEXT_MASTERCLASS.platform}
          </p>
          <div className="mt-4 flex flex-wrap items-center justify-center lg:justify-start gap-3 text-[10px] uppercase tracking-[0.2em] text-silver">
            <span className="inline-flex items-center gap-1.5"><Clock className="h-3 w-3 text-gold" /> 90 minutes live</span>
            <span className="opacity-40">·</span>
            <span className="inline-flex items-center gap-1.5"><Video className="h-3 w-3 text-gold" /> Lifetime replay</span>
            <span className="opacity-40">·</span>
            <span className="inline-flex items-center gap-1.5"><Users className="h-3 w-3 text-gold" /> Circle access</span>
          </div>
        </Reveal>
        </div>

        {/* Portrait inherits the site-wide per-breakpoint crop presets
            (--hero-anchor-x/y + --hero-zoom) so the face stays centered on
            mobile, tablet and desktop exactly as it does on the homepage. */}
        <div className="lg:col-span-5 max-w-[420px] w-full mx-auto">
          <Reveal variant="scale" delay={120}>
            <HeroPortrait
              src={heroImg}
              avifSrcSet={heroAvifSrcSet}
              webpSrcSet={heroWebpSrcSet}
              jpgSrcSet={heroJpgSrcSet}
              placeholderSrc={heroPlaceholder}
              sizes={HERO_SIZES}
              priority

              alt={IMAGE_ALT.heroMasterclass}
            />
          </Reveal>
        </div>
      </div>
    </section>


    <section className="pb-20">
      <div className="container max-w-4xl">
        <Reveal variant="scale" className="premium-card p-6 md:p-10">
          <div className="text-[10px] uppercase tracking-[0.3em] text-gold">What you get</div>
          <h2 className="font-display uppercase text-white text-2xl md:text-3xl mt-3">
            Everything, in <span className="text-gradient-gold">one seat.</span>
          </h2>
          <ul className="mt-6 grid md:grid-cols-2 gap-4">
            {INCLUDES.map(({ icon: Icon, label }) => (
              <li key={label} className="flex items-start gap-3 text-sm text-white/90">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gold/15 text-gold flex-shrink-0">
                  <Icon className="h-4 w-4" />
                </span>
                <span className="leading-relaxed">{label}</span>
              </li>
            ))}
          </ul>
          <div className="mt-8 text-center">
            <Link
              to={BOOK_URL}
              onClick={() => {
                trackMasterclassCTA("masterclass_includes_reserve", { position: "mid_page" });
                trackPlacementCTA("mid_page", PRIMARY, { surface: "masterclass_includes" });
              }}
              className="inline-flex items-center justify-center gap-2 bg-cta text-white font-display uppercase tracking-[0.2em] text-xs px-6 py-4 shadow-cta hover:brightness-110 transition-all"
            >
              {PRIMARY.label}
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>

    {/* Marquee divider — carries the acronym between "what you get" and the
        curriculum without adding another headline. */}
    <div className="relative h-10 md:h-16 mt-2 mb-12 md:mb-16 overflow-hidden" aria-hidden>
      <MakeChangeMotif variant="ticker" className="inset-y-0 flex items-center" intensity={0.75} scale={0.6} />
    </div>



    {/* Curriculum is where the acronym is actually taught, so the motif steps
        up from ambient texture to a full watermark behind the 10 pillars. The
        label is announced once for screen readers in place of the glyphs. */}
    <section className="pb-20 relative overflow-hidden">
      <MakeChangeMotif
        variant="watermark"
        intensity={0.1}
        scale={0.8}
        blur={1}
        label="The MAKECHANGE framework: Momentum, Authority, Kingdom-Focus, Equity, Clarity, Healing, Action, Navigation, Growth, Empowerment."
      />
      <div className="container max-w-4xl relative z-10">

        <Reveal variant="up" className="text-center">
          <span className="eyebrow !mx-auto">Curriculum</span>
          <h2 className="font-display uppercase text-white text-3xl md:text-4xl mt-3">
            The 10 pillars of <span className="text-gradient-gold">MAKECHANGE.</span>
          </h2>
          <p className="text-silver text-sm md:text-base mt-4 max-w-2xl mx-auto">
            One pillar at a time. Each with a working exercise, a case teardown, and a specific next move.
          </p>
        </Reveal>

        <div className="mt-10 grid md:grid-cols-2 gap-4">
          {CURRICULUM.map((c, i) => (
            <Reveal key={c.pillar} variant="up" delay={i * 40} className="border border-gold/20 bg-navy-elevated/40 p-5 hover:border-gold/50 transition-colors">
              <div className="flex items-baseline gap-3">
                <span className="font-display text-gold text-xs uppercase tracking-[0.25em]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="font-display uppercase text-white text-lg">{c.pillar}</h3>
              </div>
              <p className="text-silver text-sm mt-2 leading-relaxed">{c.promise}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="pb-24">
      <div className="container max-w-3xl">
        <Reveal variant="up" className="text-center">
          <span className="eyebrow !mx-auto">FAQ</span>
          <h2 className="font-display uppercase text-white text-3xl md:text-4xl mt-3">
            Questions, <span className="text-gradient-gold">answered.</span>
          </h2>
        </Reveal>

        <div className="mt-10 space-y-4">
          {FAQS.map((f, i) => (
            <Reveal
              key={f.q}
              variant="up"
              delay={i * 30}
              className="border border-gold/20 bg-navy-elevated/40 p-5 md:p-6"
            >
              <details className="group">
                <summary className="cursor-pointer flex items-start justify-between gap-4 list-none">
                  <h3 className="font-display uppercase text-white text-sm md:text-base tracking-wide">
                    {f.q}
                  </h3>
                  <span className="text-gold text-lg leading-none group-open:rotate-45 transition-transform">
                    +
                  </span>
                </summary>
                <p className="text-silver text-sm md:text-[15px] mt-3 leading-relaxed">
                  {f.a}
                </p>
              </details>
            </Reveal>
          ))}
        </div>

        <div className="mt-10 flex items-center justify-center gap-2 text-xs text-silver/80">
          <CheckCircle2 className="h-4 w-4 text-gold" />
          Still deciding? Take the free Scan and we'll route you to the right pathway.
        </div>

        <div className="mt-10 text-center">
          <Link
            to="/invest"
            onClick={() => trackMasterclassCTA("masterclass_compare_pathways", { position: "footer_link" })}
            className="text-silver text-xs uppercase tracking-[0.25em] hover:text-white inline-flex items-center gap-2"
          >
            <ArrowLeft className="h-3 w-3" /> Compare all pathways
          </Link>
        </div>
      </div>
    </section>

    <section className="py-16 md:py-20">
      <div className="container max-w-2xl">
        <MasterclassRegistrationForm id="register" />
      </div>
    </section>


    <ConversionBand
      eyebrow={`Reserve · $${PRICE}`}
      heading="Take the seat."
      subheading="One 90-minute session. Lifetime replay. A workbook you'll actually finish. 30 days inside the Circle."
      primaryLabel={PRIMARY.label}
      primaryHref={PRIMARY.href}
      secondaryLabel="Take the Free Scan"
      secondaryHref="/scan"
      onPrimaryClick={() =>
        {
          trackMasterclassCTA("masterclass_band_reserve", { position: "conversion_band", intent: true });
          trackPlacementCTA("final", PRIMARY, { surface: "masterclass_conversion_band" });
        }
      }
      onSecondaryClick={() =>
        trackMasterclassCTA("masterclass_band_scan", { position: "conversion_band" })
      }
    />

    <SiteFooter />
  </div>
  );
};



export default Masterclass;
